import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, 
  DollarSign, 
  Target, 
  Zap,
  Crown,
  Users,
  BarChart3
} from 'lucide-react';
import { useMarketData } from '../context/MarketDataContext';
import Navigation from './Navigation';
import MarketTicker from './MarketTicker';
import QuickBetCard from './QuickBetCard';
import PositionsList from './PositionsList';
import toast from 'react-hot-toast';

const Dashboard = () => {
  const { marketData, marketSummary } = useMarketData();
  const navigate = useNavigate();
  const [activePositions, setActivePositions] = useState([]);
  const [stats] = useState({
    balance: 5000,
    totalWinnings: 0,
    winStreak: 0,
    totalBets: 0
  });

  useEffect(() => {
    // Load user stats and positions
    loadUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUserData = async () => {
    try {
      // Load user positions
      const response = await fetch('/api/trading/positions', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const positions = await response.json();
        setActivePositions(positions);
      }
    } catch (error) {
      console.error('Failed to load user data:', error);
    }
  };

  const handleQuickBet = async (symbol, direction, amount) => {
    try {
      toast.loading('🧠 Calculating optimal position...', { id: 'quickbet' });
      
      // Calculate target gain for quick bet (2x multiplier)
      const targetGain = amount * 2;
      const timeframeMinutes = 60; // 1 hour for quick bets
      
      // First calculate the position using the new API
      const calculateResponse = await fetch('/api/trading/calculate-position', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          symbol,
          betAmount: amount,
          targetGain,
          timeframeMinutes
        })
      });

      if (!calculateResponse.ok) {
        const errorData = await calculateResponse.json();
        throw new Error(errorData.error || 'Position calculation failed');
      }

      const calculationResult = await calculateResponse.json();
      
      // Override direction if user specified one (for UP/DOWN buttons)
      if (calculationResult.calculatedPosition) {
        calculationResult.calculatedPosition.direction = direction;
      }

      toast.dismiss('quickbet');
      toast.loading('🚀 Placing bet...', { id: 'placing' });

      // Place the bet with calculated position
      const betResponse = await fetch('/api/trading/place-bet', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          symbol,
          betAmount: amount,
          targetGain,
          timeframeMinutes,
          calculatedPosition: calculationResult.calculatedPosition
        })
      });

      toast.dismiss('placing');

      if (betResponse.ok) {
        await betResponse.json();
        toast.success(`🎯 Bet placed on ${symbol}! Target: $${targetGain} in ${timeframeMinutes}m`);
        loadUserData(); // Refresh positions
      } else {
        const errorData = await betResponse.json();
        throw new Error(errorData.error || 'Bet placement failed');
      }
    } catch (error) {
      toast.dismiss();
      toast.error(`❌ ${error.message}`);
      console.error('Quick bet error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900">
      <Navigation />
      
      {/* Market Ticker */}
      <MarketTicker />
      
      <div className="container mx-auto px-4 py-8">
        {/* Welcome Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl md:text-6xl font-bold text-white neon-text mb-4">
            🎰 Welcome to TradeBet
          </h1>
          <p className="text-xl text-gray-300">
            Where Wall Street meets Vegas - and everyone wins!
          </p>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatsCard
            icon={<DollarSign className="w-8 h-8" />}
            title="Balance"
            value={`$${stats.balance.toLocaleString()}`}
            color="text-green-400"
            bgColor="from-green-800 to-green-900"
          />
          <StatsCard
            icon={<TrendingUp className="w-8 h-8" />}
            title="Total Winnings"
            value={`$${stats.totalWinnings.toLocaleString()}`}
            color="text-blue-400"
            bgColor="from-blue-800 to-blue-900"
          />
          <StatsCard
            icon={<Zap className="w-8 h-8" />}
            title="Win Streak"
            value={stats.winStreak}
            color="text-yellow-400"
            bgColor="from-yellow-800 to-yellow-900"
          />
          <StatsCard
            icon={<Target className="w-8 h-8" />}
            title="Total Bets"
            value={stats.totalBets}
            color="text-purple-400"
            bgColor="from-purple-800 to-purple-900"
          />
        </div>

        {/* Main Action - Create Custom Bet */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="casino-card p-8 text-center bg-gradient-to-r from-purple-800 to-pink-800">
            <h2 className="text-3xl font-bold text-white mb-4">🎯 Create Your Perfect Bet</h2>
            <p className="text-xl text-gray-200 mb-6">
              Tell us how much you want to bet and how much you want to win - our AI will calculate the perfect position!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/trade')}
                className="bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold py-4 px-8 rounded-lg text-xl hover:from-green-600 hover:to-emerald-600 transition-all duration-200 shadow-lg"
              >
                🚀 Start Custom Betting
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/demo')}
                className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold py-4 px-8 rounded-lg text-xl hover:from-yellow-600 hover:to-orange-600 transition-all duration-200 shadow-lg"
              >
                🎮 Try Demo
              </motion.button>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Quick Bets Section */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="casino-card p-6 mb-8"
            >
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                <Zap className="w-6 h-6 mr-2 text-yellow-400" />
                ⚡ Quick Bets - One Tap to Win!
              </h2>
              <p className="text-gray-300 mb-4 text-sm">
                Or try these pre-configured quick bets for instant action
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {marketData && Object.keys(marketData).length > 0 ? (
                  Object.entries(marketData).slice(0, 6).map(([symbol, data]) => (
                    <QuickBetCard
                      key={symbol}
                      symbol={symbol}
                      data={data}
                      onBet={handleQuickBet}
                    />
                  ))
                ) : (
                  <div className="col-span-2 text-center py-8">
                    <div className="spinner mx-auto mb-4"></div>
                    <p className="text-gray-400">Loading market data...</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Market Summary */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="casino-card p-6"
            >
              <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2 text-blue-400" />
                📊 Market Pulse
              </h3>
              
              {marketSummary && (
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Market Status:</span>
                    <span className={marketSummary.marketOpen ? 'text-green-400' : 'text-red-400'}>
                      {marketSummary.marketOpen ? '🟢 Open' : '🔴 Closed'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Avg Change:</span>
                    <span className={marketSummary.avgChange >= 0 ? 'text-green-400' : 'text-red-400'}>
                      {marketSummary.avgChange >= 0 ? '+' : ''}{marketSummary.avgChange.toFixed(2)}%
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Volatility:</span>
                    <span className="text-yellow-400">
                      {(marketSummary.avgVolatility * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
              )}
            </motion.div>

            {/* Leaderboard Preview */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              delay={0.1}
              className="casino-card p-6"
            >
              <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                <Crown className="w-5 h-5 mr-2 text-yellow-400" />
                🏆 Top Players
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-yellow-400 mr-2">🥇</span>
                    <span className="text-white text-sm">AI_Whale</span>
                  </div>
                  <span className="text-green-400 text-sm">$25,847</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-gray-400 mr-2">🥈</span>
                    <span className="text-white text-sm">TradeKing</span>
                  </div>
                  <span className="text-green-400 text-sm">$18,392</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-orange-400 mr-2">🥉</span>
                    <span className="text-white text-sm">BetMaster</span>
                  </div>
                  <span className="text-green-400 text-sm">$12,756</span>
                </div>
              </div>
              
              <button className="w-full mt-4 px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg hover:from-yellow-600 hover:to-orange-600 transition-all duration-200">
                View Full Leaderboard
              </button>
            </motion.div>

            {/* Social Activity */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              delay={0.2}
              className="casino-card p-6"
            >
              <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                <Users className="w-5 h-5 mr-2 text-purple-400" />
                🎮 Live Activity
              </h3>
              
              <div className="space-y-3 text-sm">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-400 rounded-full mr-2 pulse-dot"></div>
                  <span className="text-gray-300">
                    <span className="text-white">BetMaster</span> won $247 on TSLA
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-blue-400 rounded-full mr-2 pulse-dot"></div>
                  <span className="text-gray-300">
                    <span className="text-white">CryptoQueen</span> joined Tesla Bulls room
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full mr-2 pulse-dot"></div>
                  <span className="text-gray-300">
                    <span className="text-white">AI_Whale</span> hit 15-win streak! 🔥
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Active Positions */}
        {activePositions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            delay={0.3}
            className="mt-8"
          >
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
              <Target className="w-6 h-6 mr-2 text-green-400" />
              🎯 Your Active Bets
            </h2>
            <PositionsList 
              positions={activePositions} 
              onPositionClosed={(positionId) => {
                // Remove the closed position from the list
                setActivePositions(prev => prev.filter(p => p.id !== positionId));
                // Reload user data to get updated balance
                loadUserData();
              }}
            />
          </motion.div>
        )}
      </div>
    </div>
  );
};

// Stats Card Component
const StatsCard = ({ icon, title, value, color, bgColor }) => (
  <motion.div
    whileHover={{ scale: 1.05 }}
    className={`casino-card p-6 bg-gradient-to-br ${bgColor}`}
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-300 text-sm">{title}</p>
        <p className={`text-2xl font-bold ${color}`}>{value}</p>
      </div>
      <div className={color}>
        {icon}
      </div>
    </div>
  </motion.div>
);

export default Dashboard;